import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  DataTable,
  EmptyState,
  FullscreenSpinner,
  Input,
  Spinner,
  useToast,
  type Column,
} from '@shared/ui';
import {
  auditApi,
  KNOWN_AUDIT_ACTIONS,
  type AuditEntry,
  type AuditTargetType,
} from '@entities/audit';
import { formatDateTime } from '@shared/lib/format';
import { downloadBlob } from '@shared/lib/download';

const TARGET_TYPES: AuditTargetType[] = [
  'User',
  'Manufacturer',
  'Product',
  'BuyRequest',
  'Quote',
  'Order',
  'Message',
  'Category',
  'Carrier',
];

const COLUMNS: Column<AuditEntry>[] = [
  {
    key: 'action',
    header: 'Action',
    cell: (r) => (
      <Badge variant="outline" className="font-mono">
        {r.action}
      </Badge>
    ),
  },
  {
    key: 'target',
    header: 'Target',
    cell: (r) =>
      r.targetType ? (
        <span className="text-xs">
          {r.targetType}
          {r.targetId ? ` · ${r.targetId.slice(0, 8)}…` : ''}
        </span>
      ) : (
        '—'
      ),
  },
  {
    key: 'actor',
    header: 'Actor',
    cell: (r) => (r.actorId ? <span className="font-mono text-xs">{r.actorId.slice(0, 8)}…</span> : '—'),
  },
  { key: 'at', header: 'At', cell: (r) => formatDateTime(r.at) },
  {
    key: 'payload',
    header: '',
    cell: (r) => (r.payload ? <PayloadCell payload={r.payload} /> : null),
  },
];

function PayloadCell({ payload }: { payload: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      className="rounded border px-2 py-0.5 text-xs hover:bg-accent"
    >
      {open ? (
        <pre className="max-w-md overflow-auto whitespace-pre-wrap text-left text-[10px]">
          {JSON.stringify(payload, null, 2)}
        </pre>
      ) : (
        'payload'
      )}
    </button>
  );
}

function toIsoStart(date: string): string | undefined {
  return date ? new Date(`${date}T00:00:00.000Z`).toISOString() : undefined;
}

function toIsoEnd(date: string): string | undefined {
  return date ? new Date(`${date}T23:59:59.999Z`).toISOString() : undefined;
}

export function AuditPage() {
  const toast = useToast();
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState<AuditTargetType | ''>('');
  const [actor, setActor] = useState('');
  const [targetId, setTargetId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const filters = {
    action: action || undefined,
    targetType: targetType || undefined,
    actor: actor || undefined,
    targetId: targetId || undefined,
    from: toIsoStart(fromDate),
    to: toIsoEnd(toDate),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['audit', filters],
    queryFn: () => auditApi.list({ ...filters, size: 100 }),
  });

  const exportCsv = useMutation({
    mutationFn: () => auditApi.exportCsv(filters),
    onSuccess: (blob) => {
      downloadBlob(blob, `audit-${Math.floor(Date.now() / 1000)}.csv`);
      toast.success('CSV скачан');
    },
    onError: (e: Error) => toast.error('Экспорт не удался', e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Журнал аудита</h1>
        <Button variant="outline" onClick={() => exportCsv.mutate()} disabled={exportCsv.isPending}>
          {exportCsv.isPending ? <Spinner /> : 'Экспорт CSV'}
        </Button>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <select
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="h-10 rounded-md border bg-background px-2 text-sm"
        >
          <option value="">Все действия</option>
          {KNOWN_AUDIT_ACTIONS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <select
          value={targetType}
          onChange={(e) => setTargetType(e.target.value as AuditTargetType | '')}
          className="h-10 rounded-md border bg-background px-2 text-sm"
        >
          <option value="">Любой target</option>
          {TARGET_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <Input placeholder="Actor UUID" defaultValue={actor} onBlur={(e) => setActor(e.target.value)} />
        <Input placeholder="Target UUID" defaultValue={targetId} onBlur={(e) => setTargetId(e.target.value)} />
        <div>
          <label className="block text-[10px] uppercase tracking-wide text-muted-foreground">
            From
          </label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wide text-muted-foreground">To</label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <FullscreenSpinner />
          ) : (
            <DataTable
              rows={data?.content ?? []}
              columns={COLUMNS}
              rowKey={(r) => r.id}
              empty={<EmptyState title="Лог пуст" />}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
